import {
    ChangeEvent,
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    Autocomplete,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import {
    AddCircle,
    ArrowDownward,
    ArrowUpward,
    Delete,
} from "@mui/icons-material";

import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import { StickyBottomBar } from "../../components/common";
import {
    useCreateRecipe,
    useDeleteRecipe,
    useGetRecipeById,
    usePublishRecipe,
    useUnpublishRecipe,
    useUpdateRecipe,
    useUploadRecipeMedia,
} from "../../hooks";
import { useMessage } from "../../sdk/hooks";
import {
    IRecipeIngredientInput,
    IRecipeStepInput,
} from "../../sdk/utils/api/recipes";
import {
    RecipeDietType,
    RecipeDifficulty,
    RecipeSource,
    RecipeStatus,
    recipeDietTypeNames,
    recipeDifficultyNames,
    recipeIngredientUnits,
    recipeSourceNames,
    recipeStatusNames,
} from "../../utils/enums";

import {
    IIngredientOption,
    IngredientAutocomplete,
} from "./IngredientAutocomplete";
import { RecipeTagsSelect } from "./RecipeTagsSelect";

const Root = styled("main")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};

    padding: ${({ theme }) => theme.spacing(3)};
    max-width: 900px;
    margin: ${({ theme }) => theme.spacing(0, "auto")};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TitleGroup = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled(Typography)`
    font-size: 22px;
    font-weight: 600;
`;

const SectionTitle = styled(Typography)`
    font-size: 16px;
    font-weight: 500;
    color: #626262;
    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const SectionContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};

    & > * {
        flex: 1;
    }
`;

const HeroImagePreview = styled("img")`
    width: 160px;
    height: 160px;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.spacing(1)};
    border: 1px solid #d9d9d9;
`;

const HeroImageEmpty = styled("div")`
    width: 160px;
    height: 160px;
    border-radius: ${({ theme }) => theme.spacing(1)};
    border: 1px dashed #bfbfbf;
    display: flex;
    align-items: center;
    justify-content: center;
    color: grey;
    font-size: 13px;
    text-align: center;
    padding: ${({ theme }) => theme.spacing(1)};
`;

const HeroImageRow = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;
`;

const CardRow = styled(Paper)`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: flex-start;
    padding: ${({ theme }) => theme.spacing(2)};
`;

const CardFields = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
    flex: 1;
`;

const CardActions = styled("div")`
    display: flex;
    flex-direction: column;
`;

const StepIndexLabel = styled(Typography)`
    font-size: 13px;
    color: grey;
    min-width: 24px;
    padding-top: ${({ theme }) => theme.spacing(1)};
`;

interface IIngredientFormValue {
    ingredient: IIngredientOption | null;
    quantity: string;
    unit: string;
    preparation: string;
    optional: boolean;
}

interface IStepFormValue {
    instruction: string;
    hasTimer: boolean;
    durationSeconds: string;
    tip: string;
}

interface IRecipeFormValues {
    title: string;
    slug: string;
    source: RecipeSource;
    subtitle: string;
    description: string;
    heroAssetId: string | null;
    difficulty: RecipeDifficulty;
    dietType: RecipeDietType;
    cuisine: string;
    prepTimeMinutes: number;
    cookTimeMinutes: number;
    totalTimeMinutes: number;
    baseServings: number;
    minServings: number;
    maxServings: number;
    ingredients: IIngredientFormValue[];
    steps: IStepFormValue[];
    tagIds: string[];
}

const emptyIngredient: IIngredientFormValue = {
    ingredient: null,
    quantity: "",
    unit: "",
    preparation: "",
    optional: false,
};

const emptyStep: IStepFormValue = {
    instruction: "",
    hasTimer: false,
    durationSeconds: "",
    tip: "",
};

const initialValues: IRecipeFormValues = {
    title: "",
    slug: "",
    source: RecipeSource.COMPANY,
    subtitle: "",
    description: "",
    heroAssetId: null,
    difficulty: RecipeDifficulty.EASY,
    dietType: RecipeDietType.VEG,
    cuisine: "",
    prepTimeMinutes: 0,
    cookTimeMinutes: 0,
    totalTimeMinutes: 0,
    baseServings: 2,
    minServings: 1,
    maxServings: 12,
    ingredients: [],
    steps: [],
    tagIds: [],
};

const validationSchema = Yup.object({
    title: Yup.string().min(1).required("Title is required"),
    source: Yup.string().required(),
    difficulty: Yup.number().required(),
    dietType: Yup.string().required(),
    prepTimeMinutes: Yup.number().min(0).required(),
    cookTimeMinutes: Yup.number().min(0).required(),
    totalTimeMinutes: Yup.number().min(0).required(),
    baseServings: Yup.number().min(1).required(),
    minServings: Yup.number().min(1).required(),
    maxServings: Yup.number().min(1).required(),
    ingredients: Yup.array().of(
        Yup.object({
            ingredient: Yup.object()
                .nullable()
                .required("Select an ingredient"),
        }),
    ),
    steps: Yup.array().of(
        Yup.object({
            instruction: Yup.string()
                .min(1)
                .required("Instruction is required"),
        }),
    ),
});

const statusChipColor: Record<RecipeStatus, "default" | "success" | "warning"> =
    {
        [RecipeStatus.DRAFT]: "warning",
        [RecipeStatus.PUBLISHED]: "success",
        [RecipeStatus.ARCHIVED]: "default",
    };

export const RecipeScreen: FunctionComponent = (): ReactElement => {
    const params = useParams();
    const navigate = useNavigate();
    const isNewRecipe = params.id === "new";
    const recipeId = params.id ?? "";

    const { showError, showSuccess } = useMessage();

    const recipeQuery = useGetRecipeById(recipeId);
    const createRecipeMutation = useCreateRecipe();
    const updateRecipeMutation = useUpdateRecipe(["recipe", "recipes"]);
    const deleteRecipeMutation = useDeleteRecipe("recipes");
    const publishRecipeMutation = usePublishRecipe(["recipe", "recipes"]);
    const unpublishRecipeMutation = useUnpublishRecipe(["recipe", "recipes"]);
    const uploadRecipeMediaMutation = useUploadRecipeMedia();

    const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(null);

    const {
        values,
        handleChange,
        setFieldValue,
        handleSubmit,
        errors,
        touched,
        handleBlur,
    } = useFormik<IRecipeFormValues>({
        initialValues,
        validationSchema,
        onSubmit: async (formValues) => {
            const payload = {
                title: formValues.title,
                slug: formValues.slug || undefined,
                source: formValues.source,
                subtitle: formValues.subtitle || null,
                description: formValues.description || null,
                heroAssetId: formValues.heroAssetId,
                difficulty: formValues.difficulty,
                dietType: formValues.dietType,
                cuisine: formValues.cuisine || null,
                prepTimeMinutes: formValues.prepTimeMinutes,
                cookTimeMinutes: formValues.cookTimeMinutes,
                totalTimeMinutes: formValues.totalTimeMinutes,
                baseServings: formValues.baseServings,
                minServings: formValues.minServings,
                maxServings: formValues.maxServings,
                ingredients: formValues.ingredients
                    .filter((ingredient) => ingredient.ingredient)
                    .map(
                        (ingredient): IRecipeIngredientInput => ({
                            ingredientId: ingredient.ingredient!.id,
                            quantity: ingredient.quantity
                                ? Number(ingredient.quantity)
                                : null,
                            unit: ingredient.unit || null,
                            preparation: ingredient.preparation || null,
                            optional: ingredient.optional,
                        }),
                    ),
                steps: formValues.steps.map(
                    (step): IRecipeStepInput => ({
                        instruction: step.instruction,
                        hasTimer: step.hasTimer,
                        durationSeconds:
                            step.hasTimer && step.durationSeconds
                                ? Number(step.durationSeconds)
                                : null,
                        tip: step.tip || null,
                    }),
                ),
                tagIds: formValues.tagIds,
            };

            if (isNewRecipe) {
                await createRecipeMutation.mutateAsync(payload, {
                    onSuccess: (created) => {
                        showSuccess("Recipe created successfully");
                        navigate(`/recipes/${created.id}`);
                    },
                    onError: (error: any) => {
                        showError(
                            error?.response?.data?.message ??
                                "Could not create recipe",
                        );
                    },
                });
            } else {
                await updateRecipeMutation.mutateAsync(
                    { id: recipeId, ...payload },
                    {
                        onSuccess: () =>
                            showSuccess("Recipe updated successfully"),
                        onError: (error: any) => {
                            showError(
                                error?.response?.data?.message ??
                                    "Could not update recipe",
                            );
                        },
                    },
                );
            }
        },
    });

    useEffect(() => {
        if (isNewRecipe || !recipeQuery.data) return;

        const recipe = recipeQuery.data;
        setFieldValue("title", recipe.title);
        setFieldValue("slug", recipe.slug);
        setFieldValue("source", recipe.source);
        setFieldValue("subtitle", recipe.subtitle ?? "");
        setFieldValue("description", recipe.description ?? "");
        setFieldValue("heroAssetId", recipe.heroAssetId);
        setFieldValue("difficulty", recipe.difficulty);
        setFieldValue("dietType", recipe.dietType);
        setFieldValue("cuisine", recipe.cuisine ?? "");
        setFieldValue("prepTimeMinutes", recipe.prepTimeMinutes);
        setFieldValue("cookTimeMinutes", recipe.cookTimeMinutes);
        setFieldValue("totalTimeMinutes", recipe.totalTimeMinutes);
        setFieldValue("baseServings", recipe.baseServings);
        setFieldValue("minServings", recipe.minServings);
        setFieldValue("maxServings", recipe.maxServings);
        setFieldValue(
            "ingredients",
            recipe.ingredients.map(
                (ingredient): IIngredientFormValue => ({
                    ingredient: {
                        id: ingredient.ingredientId,
                        label: ingredient.name,
                    },
                    quantity:
                        ingredient.quantity !== null
                            ? String(ingredient.quantity)
                            : "",
                    unit: ingredient.unit ?? "",
                    preparation: ingredient.preparation ?? "",
                    optional: ingredient.optional,
                }),
            ),
        );
        setFieldValue(
            "steps",
            recipe.steps.map(
                (step): IStepFormValue => ({
                    instruction: step.instruction,
                    hasTimer: step.hasTimer,
                    durationSeconds:
                        step.durationSeconds !== null
                            ? String(step.durationSeconds)
                            : "",
                    tip: step.tip ?? "",
                }),
            ),
        );
        setFieldValue(
            "tagIds",
            recipe.tags.map((tag) => tag.id),
        );
        setHeroPreviewUrl(recipe.heroImageUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipeQuery.data, isNewRecipe]);

    const handleTimeChange = useCallback(
        (field: "prepTimeMinutes" | "cookTimeMinutes") =>
            (event: ChangeEvent<HTMLInputElement>) => {
                const nextValue = Number(event.target.value) || 0;
                setFieldValue(field, nextValue);
                const otherValue =
                    field === "prepTimeMinutes"
                        ? values.cookTimeMinutes
                        : values.prepTimeMinutes;
                setFieldValue("totalTimeMinutes", nextValue + otherValue);
            },
        [setFieldValue, values.cookTimeMinutes, values.prepTimeMinutes],
    );

    const handleHeroImageChange = useCallback(
        async (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            try {
                const [uploaded] = await uploadRecipeMediaMutation.mutateAsync({
                    files: [file],
                });
                setFieldValue("heroAssetId", uploaded.id);
                setHeroPreviewUrl(uploaded.url);
            } catch {
                showError("Could not upload the hero image");
            } finally {
                event.target.value = "";
            }
        },
        [setFieldValue, showError, uploadRecipeMediaMutation],
    );

    const handleAddIngredient = useCallback(() => {
        setFieldValue("ingredients", [
            ...values.ingredients,
            { ...emptyIngredient },
        ]);
    }, [setFieldValue, values.ingredients]);

    const handleRemoveIngredient = useCallback(
        (index: number) => {
            setFieldValue(
                "ingredients",
                values.ingredients.filter(
                    (_, itemIndex) => itemIndex !== index,
                ),
            );
        },
        [setFieldValue, values.ingredients],
    );

    const handleAddStep = useCallback(() => {
        setFieldValue("steps", [...values.steps, { ...emptyStep }]);
    }, [setFieldValue, values.steps]);

    const handleRemoveStep = useCallback(
        (index: number) => {
            setFieldValue(
                "steps",
                values.steps.filter((_, itemIndex) => itemIndex !== index),
            );
        },
        [setFieldValue, values.steps],
    );

    const handleMoveStep = useCallback(
        (index: number, direction: -1 | 1) => {
            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= values.steps.length) return;

            const nextSteps = [...values.steps];
            [nextSteps[index], nextSteps[targetIndex]] = [
                nextSteps[targetIndex],
                nextSteps[index],
            ];
            setFieldValue("steps", nextSteps);
        },
        [setFieldValue, values.steps],
    );

    const handleDelete = useCallback(() => {
        deleteRecipeMutation.mutate(
            { id: recipeId },
            {
                onSuccess: () => {
                    showSuccess("Recipe deleted successfully");
                    navigate("/recipes");
                },
            },
        );
    }, [deleteRecipeMutation, navigate, recipeId, showSuccess]);

    const handleDiscard = useCallback(() => {
        navigate("/recipes");
    }, [navigate]);

    const handlePublish = useCallback(() => {
        publishRecipeMutation.mutate(
            { id: recipeId },
            {
                onSuccess: () => showSuccess("Recipe published"),
                onError: (error: any) =>
                    showError(
                        error?.response?.data?.message ??
                            "Could not publish recipe",
                    ),
            },
        );
    }, [publishRecipeMutation, recipeId, showError, showSuccess]);

    const handleUnpublish = useCallback(() => {
        unpublishRecipeMutation.mutate(
            { id: recipeId },
            { onSuccess: () => showSuccess("Recipe unpublished") },
        );
    }, [recipeId, showSuccess, unpublishRecipeMutation]);

    return (
        <>
            <Root>
                <TitleContainer>
                    <TitleGroup>
                        <Title>
                            {isNewRecipe ? "Create recipe" : "Edit recipe"}
                        </Title>
                        {!isNewRecipe && recipeQuery.data && (
                            <Chip
                                size="small"
                                label={
                                    recipeStatusNames[recipeQuery.data.status]
                                }
                                color={statusChipColor[recipeQuery.data.status]}
                            />
                        )}
                    </TitleGroup>
                    {!isNewRecipe && recipeQuery.data && (
                        <>
                            {recipeQuery.data.status ===
                            RecipeStatus.PUBLISHED ? (
                                <Button
                                    variant="outlined"
                                    onClick={handleUnpublish}
                                    disabled={unpublishRecipeMutation.isLoading}
                                >
                                    Unpublish
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handlePublish}
                                    disabled={publishRecipeMutation.isLoading}
                                >
                                    Publish
                                </Button>
                            )}
                        </>
                    )}
                </TitleContainer>

                <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
                    <SectionTitle>General</SectionTitle>
                    <SectionContainer>
                        <Row>
                            <TextField
                                required
                                size="small"
                                name="title"
                                label="Title"
                                value={values.title}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(errors.title) && touched.title}
                                helperText={touched.title && errors.title}
                            />
                            <TextField
                                size="small"
                                name="slug"
                                label="Slug"
                                placeholder="Auto-generated from title if left blank"
                                value={values.slug}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </Row>
                        <Row>
                            <TextField
                                size="small"
                                name="subtitle"
                                label="Subtitle"
                                value={values.subtitle}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <TextField
                                size="small"
                                name="cuisine"
                                label="Cuisine"
                                value={values.cuisine}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </Row>
                        <Row>
                            <TextField
                                select
                                size="small"
                                name="difficulty"
                                label="Difficulty"
                                value={values.difficulty}
                                onChange={handleChange}
                            >
                                {Object.values(RecipeDifficulty)
                                    .filter(
                                        (value): value is RecipeDifficulty =>
                                            typeof value === "number",
                                    )
                                    .map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {recipeDifficultyNames[value]}
                                        </MenuItem>
                                    ))}
                            </TextField>
                            <TextField
                                select
                                size="small"
                                name="dietType"
                                label="Diet type"
                                value={values.dietType}
                                onChange={handleChange}
                            >
                                {Object.values(RecipeDietType).map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {recipeDietTypeNames[value]}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                size="small"
                                name="source"
                                label="Source"
                                value={values.source}
                                onChange={handleChange}
                            >
                                {Object.values(RecipeSource).map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {recipeSourceNames[value]}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Row>
                        <TextField
                            multiline
                            rows={4}
                            size="small"
                            name="description"
                            label="Description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </SectionContainer>
                </Paper>

                <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
                    <SectionTitle>Hero image</SectionTitle>
                    <HeroImageRow>
                        {heroPreviewUrl ? (
                            <HeroImagePreview
                                src={heroPreviewUrl}
                                alt="Recipe hero"
                            />
                        ) : (
                            <HeroImageEmpty>No image uploaded</HeroImageEmpty>
                        )}
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={uploadRecipeMediaMutation.isLoading}
                        >
                            {uploadRecipeMediaMutation.isLoading
                                ? "Uploading..."
                                : "Upload image"}
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={handleHeroImageChange}
                            />
                        </Button>
                    </HeroImageRow>
                </Paper>

                <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
                    <SectionTitle>Time & servings</SectionTitle>
                    <Row>
                        <TextField
                            size="small"
                            type="number"
                            name="prepTimeMinutes"
                            label="Prep time (minutes)"
                            value={values.prepTimeMinutes}
                            onChange={handleTimeChange("prepTimeMinutes")}
                        />
                        <TextField
                            size="small"
                            type="number"
                            name="cookTimeMinutes"
                            label="Cook time (minutes)"
                            value={values.cookTimeMinutes}
                            onChange={handleTimeChange("cookTimeMinutes")}
                        />
                        <TextField
                            size="small"
                            type="number"
                            name="totalTimeMinutes"
                            label="Total time (minutes)"
                            value={values.totalTimeMinutes}
                            onChange={handleChange}
                        />
                    </Row>
                    <Row sx={{ mt: 2 }}>
                        <TextField
                            size="small"
                            type="number"
                            name="baseServings"
                            label="Servings"
                            value={values.baseServings}
                            onChange={handleChange}
                        />
                        <TextField
                            size="small"
                            type="number"
                            name="minServings"
                            label="Min servings"
                            value={values.minServings}
                            onChange={handleChange}
                        />
                        <TextField
                            size="small"
                            type="number"
                            name="maxServings"
                            label="Max servings"
                            value={values.maxServings}
                            onChange={handleChange}
                        />
                    </Row>
                </Paper>

                <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
                    <SectionTitle>Ingredients</SectionTitle>
                    <SectionContainer>
                        {values.ingredients.map((ingredient, index) => (
                            <CardRow
                                key={index}
                                elevation={0}
                                variant="outlined"
                            >
                                <CardFields>
                                    <IngredientAutocomplete
                                        value={ingredient.ingredient}
                                        onChange={(value) =>
                                            setFieldValue(
                                                `ingredients[${index}].ingredient`,
                                                value,
                                            )
                                        }
                                        error={Boolean(
                                            (errors.ingredients?.[index] as any)
                                                ?.ingredient,
                                        )}
                                        helperText={
                                            (errors.ingredients?.[index] as any)
                                                ?.ingredient
                                        }
                                    />
                                    <Row>
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Quantity"
                                            value={ingredient.quantity}
                                            onChange={(event) =>
                                                setFieldValue(
                                                    `ingredients[${index}].quantity`,
                                                    event.target.value,
                                                )
                                            }
                                        />
                                        {/*
                                          * Suggests the units the shopping
                                          * list can convert, while still
                                          * allowing free text — existing
                                          * recipes use words like "cloves"
                                          * and "handful".
                                          */}
                                        <Autocomplete
                                            freeSolo
                                            size="small"
                                            options={recipeIngredientUnits}
                                            value={ingredient.unit}
                                            onInputChange={(_event, value) =>
                                                setFieldValue(
                                                    `ingredients[${index}].unit`,
                                                    value,
                                                )
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Unit"
                                                    placeholder="cup, tbsp, g..."
                                                />
                                            )}
                                        />
                                        <TextField
                                            size="small"
                                            label="Preparation"
                                            placeholder="diced, melted..."
                                            value={ingredient.preparation}
                                            onChange={(event) =>
                                                setFieldValue(
                                                    `ingredients[${index}].preparation`,
                                                    event.target.value,
                                                )
                                            }
                                        />
                                    </Row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={ingredient.optional}
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        `ingredients[${index}].optional`,
                                                        event.target.checked,
                                                    )
                                                }
                                            />
                                        }
                                        label="Optional"
                                    />
                                </CardFields>
                                <CardActions>
                                    <IconButton
                                        onClick={() =>
                                            handleRemoveIngredient(index)
                                        }
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </CardActions>
                            </CardRow>
                        ))}
                        <Button
                            startIcon={<AddCircle />}
                            variant="outlined"
                            onClick={handleAddIngredient}
                        >
                            Add ingredient
                        </Button>
                    </SectionContainer>
                </Paper>

                <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
                    <SectionTitle>Steps</SectionTitle>
                    <SectionContainer>
                        {values.steps.map((step, index) => (
                            <CardRow
                                key={index}
                                elevation={0}
                                variant="outlined"
                            >
                                <StepIndexLabel>
                                    {String(index + 1).padStart(2, "0")}
                                </StepIndexLabel>
                                <CardFields>
                                    <TextField
                                        multiline
                                        rows={2}
                                        size="small"
                                        label="Instruction"
                                        value={step.instruction}
                                        onChange={(event) =>
                                            setFieldValue(
                                                `steps[${index}].instruction`,
                                                event.target.value,
                                            )
                                        }
                                        error={Boolean(
                                            (errors.steps?.[index] as any)
                                                ?.instruction,
                                        )}
                                        helperText={
                                            (errors.steps?.[index] as any)
                                                ?.instruction
                                        }
                                    />
                                    <Row>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={step.hasTimer}
                                                    onChange={(event) =>
                                                        setFieldValue(
                                                            `steps[${index}].hasTimer`,
                                                            event.target
                                                                .checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label="Has timer"
                                        />
                                        {step.hasTimer && (
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Duration (seconds)"
                                                value={step.durationSeconds}
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        `steps[${index}].durationSeconds`,
                                                        event.target.value,
                                                    )
                                                }
                                            />
                                        )}
                                    </Row>
                                    <TextField
                                        size="small"
                                        label="Tip"
                                        value={step.tip}
                                        onChange={(event) =>
                                            setFieldValue(
                                                `steps[${index}].tip`,
                                                event.target.value,
                                            )
                                        }
                                    />
                                </CardFields>
                                <CardActions>
                                    <IconButton
                                        disabled={index === 0}
                                        onClick={() =>
                                            handleMoveStep(index, -1)
                                        }
                                    >
                                        <ArrowUpward fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        disabled={
                                            index === values.steps.length - 1
                                        }
                                        onClick={() => handleMoveStep(index, 1)}
                                    >
                                        <ArrowDownward fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleRemoveStep(index)}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </CardActions>
                            </CardRow>
                        ))}
                        <Button
                            startIcon={<AddCircle />}
                            variant="outlined"
                            onClick={handleAddStep}
                        >
                            Add step
                        </Button>
                    </SectionContainer>
                </Paper>

                <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
                    <SectionTitle>Tags</SectionTitle>
                    <RecipeTagsSelect
                        value={values.tagIds}
                        onChange={(tagIds) => setFieldValue("tagIds", tagIds)}
                    />
                </Paper>
            </Root>

            {isNewRecipe ? (
                <StickyBottomBar
                    cancelText="Discard"
                    submitText="Save"
                    onCancel={handleDiscard}
                    onSubmit={handleSubmit}
                    loading={createRecipeMutation.isLoading}
                />
            ) : (
                <StickyBottomBar
                    cancelText="Delete"
                    submitText="Save"
                    onCancel={handleDelete}
                    onSubmit={handleSubmit}
                    loading={updateRecipeMutation.isLoading}
                />
            )}
        </>
    );
};
